export type AboutAuthorBindMode = "entry_primary" | "entry_reviewer" | "picked";

export type AboutAuthorSocialLink = {
	network: string;
	url: string;
};

/** One resolved person for the about-author card (host supplies at preview/publish). */
export type AboutAuthorPerson = {
	archiveUrl: string | null;
	bio: string | null;
	email: string | null;
	id: string;
	imageUrl: string | null;
	isVerified: boolean;
	name: string;
	profileUrl: string | null;
	socialLinks: AboutAuthorSocialLink[];
	title: string | null;
};

export const ABOUT_AUTHOR_DEFAULT_EYEBROW = "About the author";
export const ABOUT_AUTHOR_DEFAULT_VIEW_ALL = "View all articles";
export const ABOUT_AUTHOR_DEFAULT_VERIFIED_LABEL = "Verified author";

export const ABOUT_AUTHOR_EDITOR_PLACEHOLDER: AboutAuthorPerson = {
	id: "placeholder-author",
	name: "Alex Morgan",
	title: "Senior Editor",
	bio: "Alex writes explainers on tax, money, and consumer rights, with a focus on clear sourcing and practical next steps.",
	imageUrl: null,
	profileUrl: "/author/alex-morgan",
	archiveUrl: "/author/alex-morgan",
	email: "alex@example.com",
	isVerified: true,
	socialLinks: [{ network: "linkedin", url: "https://www.linkedin.com" }],
};

export function normalizeAboutAuthorSocialLinks(
	raw: unknown
): AboutAuthorSocialLink[] {
	if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
		return [];
	}
	const links: AboutAuthorSocialLink[] = [];
	for (const [network, value] of Object.entries(
		raw as Record<string, unknown>
	)) {
		if (typeof value !== "string") {
			continue;
		}
		const url = value.trim();
		const key = network.trim();
		if (!(key && url)) {
			continue;
		}
		links.push({ network: key, url });
	}
	return links;
}

export function resolveAboutAuthorEyebrow(stored: string | undefined): string {
	if (stored === undefined) {
		return ABOUT_AUTHOR_DEFAULT_EYEBROW;
	}
	return stored.trim();
}

export function resolveAboutAuthorViewAllLabel(
	stored: string | undefined
): string {
	if (stored === undefined) {
		return ABOUT_AUTHOR_DEFAULT_VIEW_ALL;
	}
	return stored.trim();
}

export function pickAboutAuthorPerson(
	config: { authorId?: string; bindMode?: AboutAuthorBindMode },
	people: AboutAuthorPerson[],
	primaryId?: string | null,
	reviewerId?: string | null
): AboutAuthorPerson | null {
	if (people.length === 0) {
		return null;
	}
	const mode = config.bindMode ?? "entry_primary";
	if (mode === "picked") {
		const wanted = config.authorId?.trim() ?? "";
		return people.find((person) => person.id === wanted) ?? people[0] ?? null;
	}
	if (mode === "entry_reviewer") {
		if (reviewerId) {
			return people.find((person) => person.id === reviewerId) ?? null;
		}
		return people.length > 1 ? (people[1] ?? null) : null;
	}
	if (primaryId) {
		return (
			people.find((person) => person.id === primaryId) ?? people[0] ?? null
		);
	}
	return people[0] ?? null;
}

function optionalString(value: unknown): string | null {
	return typeof value === "string" && value.trim() ? value.trim() : null;
}

export function parseAboutAuthorResolvedJson(
	raw: unknown
): AboutAuthorPerson | null {
	if (typeof raw !== "string" || !raw.trim()) {
		return null;
	}
	try {
		const parsed: unknown = JSON.parse(raw);
		if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
			return null;
		}
		const o = parsed as Record<string, unknown>;
		const name = typeof o.name === "string" ? o.name.trim() : "";
		if (!name) {
			return null;
		}
		const socialsRaw = o.socialLinks;
		const socialLinks = Array.isArray(socialsRaw)
			? socialsRaw.flatMap((item) => {
					if (!item || typeof item !== "object") {
						return [];
					}
					const rec = item as Record<string, unknown>;
					const network =
						typeof rec.network === "string" ? rec.network.trim() : "";
					const url = typeof rec.url === "string" ? rec.url.trim() : "";
					return network && url ? [{ network, url }] : [];
				})
			: normalizeAboutAuthorSocialLinks(socialsRaw);
		return {
			id: typeof o.id === "string" ? o.id : "",
			name,
			title: optionalString(o.title),
			bio: optionalString(o.bio),
			imageUrl: optionalString(o.imageUrl),
			profileUrl: optionalString(o.profileUrl),
			archiveUrl: optionalString(o.archiveUrl),
			email: optionalString(o.email),
			isVerified: o.isVerified === true,
			socialLinks,
		};
	} catch {
		return null;
	}
}

export function resolveAboutAuthorViewAllHref(
	person: AboutAuthorPerson,
	urlOverride?: string
): string {
	const override = urlOverride?.trim() ?? "";
	if (override) {
		return override;
	}
	return person.archiveUrl?.trim() || person.profileUrl?.trim() || "";
}

/** Host / API row → card person. Empty strings become hidden slots. */
export function aboutAuthorPersonFromFields(input: {
	archiveUrl?: string | null;
	bio?: string | null;
	email?: string | null;
	id: string;
	imageUrl?: string | null;
	isVerified?: boolean;
	name: string;
	profileUrl?: string | null;
	socialLinks?: unknown;
	title?: string | null;
}): AboutAuthorPerson {
	return {
		id: input.id,
		name: input.name.trim() || "Author",
		title: optionalString(input.title),
		bio: optionalString(input.bio),
		imageUrl: optionalString(input.imageUrl),
		profileUrl: optionalString(input.profileUrl),
		archiveUrl: optionalString(input.archiveUrl),
		email: optionalString(input.email),
		isVerified: input.isVerified === true,
		socialLinks: normalizeAboutAuthorSocialLinks(input.socialLinks),
	};
}

/** Canvas fallback when the host has not passed a people list yet. */
export function aboutAuthorPeopleFromEntryMeta(meta: {
	authorImageUrl: string | null;
	authorName: string;
	authorProfileUrl: string | null;
	authorTitle: string | null;
	reviewerImageUrl: string | null;
	reviewerName: string | null;
	reviewerProfileUrl: string | null;
	reviewerTitle: string | null;
}): AboutAuthorPerson[] {
	const people: AboutAuthorPerson[] = [];
	if (meta.authorName.trim()) {
		people.push(
			aboutAuthorPersonFromFields({
				id: "entry-primary",
				name: meta.authorName,
				title: meta.authorTitle,
				imageUrl: meta.authorImageUrl,
				profileUrl: meta.authorProfileUrl,
				archiveUrl: meta.authorProfileUrl,
			})
		);
	}
	if (meta.reviewerName?.trim()) {
		people.push(
			aboutAuthorPersonFromFields({
				id: "entry-reviewer",
				name: meta.reviewerName,
				title: meta.reviewerTitle,
				imageUrl: meta.reviewerImageUrl,
				profileUrl: meta.reviewerProfileUrl,
				archiveUrl: meta.reviewerProfileUrl,
			})
		);
	}
	return people;
}

export function parseAboutAuthorConfigJson(raw: unknown): Partial<{
	authorId?: string;
	avatarSize?: string;
	bindMode?: AboutAuthorBindMode;
	eyebrowLabel?: string;
	linkProfile?: boolean;
	showAvatar?: boolean;
	showBio?: boolean;
	showSocials?: boolean;
	showVerifiedBadge?: boolean;
	showViewAll?: boolean;
	verifiedBadgeLabel?: string;
	viewAllLabel?: string;
	viewAllUrl?: string;
}> {
	if (typeof raw !== "string" || !raw.trim()) {
		return {};
	}
	try {
		const parsed: unknown = JSON.parse(raw);
		if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
			return parsed as ReturnType<typeof parseAboutAuthorConfigJson>;
		}
	} catch {
		/* ignore */
	}
	return {};
}
